import { Selection, CheckboxVisibility, CommandBar, Label, Link, MarqueeSelection, SelectionMode, ShimmeredDetailsList, mergeStyleSets, ColumnActionsMode, Panel, PanelType, Toggle, Checkbox, DetailsListLayoutMode, ConstrainMode } from "@fluentui/react";
import { read } from "fs";
import { inject, observer } from "mobx-react";
import { Component, ReactNode } from "react";
import { toast } from "react-toastify";
import { isNoSubstitutionTemplateLiteral } from "typescript";
import { ErrorInfo } from "../../generated";
import { InjectedProps } from "../InjectedProps";
import EditRaceTrack from "../RaceTracks/EditRaceTrack";
import ChangeReadPointAssignment from "../ReadPoints/ChangeReadPointAssignment";
import CreateReadPoint from "../ReadPoints/CreateReadPoint";
import EditReadPoint from "../ReadPoints/EditReadPoint";
import { IRaceTrackInfo } from "./RaceTrackList";
import PasswordIcon from '@mui/icons-material/Password';
import ChangePassword from "../Users/ChangePassword";

export interface IReadPointInfo {
    key: string,
    name: string,
    isIntermediate: boolean,
    assignedRaceTracks: IRaceTrackInfo[] | undefined,
    distanceToStart: number | undefined,
    account: string | undefined,
    accountId: string | number | undefined,
}

interface ReadPointsListState {
    knownReadPoints: IReadPointInfo[],
    selectionDetails: string,
    isEditPanelOpen: boolean,
    isNewPanelOpen: boolean,
    isEditAssignmentPanelOpen: boolean,
    readPointId: string | undefined,
    isChangePasswordPanelOpen: boolean,
    changePasswordAccountKey: string | number | undefined,
}

@inject("userStore")
@inject("webApiClient")
@inject("signalRClient")
@observer
export default class ReadPointsList extends Component<InjectedProps, ReadPointsListState> {
    static defaultProps = {} as InjectedProps;

    private listSelection: Selection;
    classNames = mergeStyleSets({
        controlWrapper: {
            display: 'flex',
            flexWrap: 'wrap',
        },
        selectionDetails: {
            marginBottom: '20px',
        },
    });
    controlStyles = {
        root: {
            margin: '0 30px 20px 0',
            maxWidth: '300px',
        },
    };

    /**
     *
     */
    constructor(props: InjectedProps) {
        super(props);

        this.ListItemInvoked = this.ListItemInvoked.bind(this);
        this.ReadPointChanged = this.ReadPointChanged.bind(this);
        this.ReadPointChangeCanceled = this.ReadPointChangeCanceled.bind(this);
        this.DownloadReadPoints = this.DownloadReadPoints.bind(this);
        this.UpdateCurrentReadPointsAsync = this.UpdateCurrentReadPointsAsync.bind(this);
        this.ChangePassword = this.ChangePassword.bind(this);

        this.listSelection = new Selection({
            onSelectionChanged: () => this.setState({ ...this.state, selectionDetails: this.GetSelectionDetails() })
        });

        this.state = {
            knownReadPoints: [],
            selectionDetails: this.GetSelectionDetails(),
            isEditPanelOpen: false,
            readPointId: undefined,
            isNewPanelOpen: false,
            isEditAssignmentPanelOpen: false,
            isChangePasswordPanelOpen: false,
            changePasswordAccountKey: undefined,
        }

        this.ToggleNewPanel = this.ToggleNewPanel.bind(this);
    }

    componentDidMount(): void {
        this.UpdateCurrentReadPointsAsync();
    }

    render(): ReactNode {
        return (<div style={{ marginTop: "10px", marginLeft: "30px" }} data-is-scrollable="true">
            {this.RenderHeader()}
            {this.RenderToolbar()}
            {this.RenderTable()}
            {this.RenderEditPanel()}
            {this.RenderPasswordChangePanel()}
            {this.RenderNewPanel()}
            {this.RenderAssignmentPanel()}
        </div>);
    }

    private RenderNewPanel(): ReactNode {
        if (this.state.isNewPanelOpen) {
            return (<Panel
                type={PanelType.medium}
                isBlocking={true}
                isOpen={this.state.isNewPanelOpen}
                hasCloseButton={false}
                // You MUST provide this prop! Otherwise screen readers will just say "button" with no label.
                closeButtonAriaLabel="Close">
                <CreateReadPoint onCreated={this.ReadPointChanged} onCancel={this.ReadPointChangeCanceled} />
            </Panel>)
        }
        else {
            <div />
        }
    }

    private RenderAssignmentPanel(): ReactNode {
        if (this.state.isEditAssignmentPanelOpen) {
            return (
                <Panel
                    type={PanelType.medium}
                    isBlocking={true}
                    isOpen={this.state.isEditAssignmentPanelOpen}
                    hasCloseButton={false}
                    closeButtonAriaLabel="Close">
                    <ChangeReadPointAssignment onUpdated={this.ReadPointChanged} onCancel={this.ReadPointChangeCanceled} readPointId={this.state.readPointId} />
                </Panel>
            )
        }
        else {
            return null;
        }
    }

    private RenderPasswordChangePanel(): ReactNode {
        if (this.state.isChangePasswordPanelOpen && this.state.changePasswordAccountKey !== undefined) {
            return (<Panel
                type={PanelType.custom}
                customWidth="40vw"
                isBlocking={true}
                isOpen={this.state.isChangePasswordPanelOpen}
                hasCloseButton={false}
                // You MUST provide this prop! Otherwise screen readers will just say "button" with no label.
                closeButtonAriaLabel="Close">
                <ChangePassword onCancel={this.ReadPointChangeCanceled} onUpdated={this.ReadPointChanged} accountId={this.state.changePasswordAccountKey} />
            </Panel>)
        }
    }
    
    private GetSelectionDetails(): string {
        const selectionCount = this.listSelection.getSelectedCount();

        switch (selectionCount) {
            case 0:
                return 'Kein Eintrag ausgewählt';
            case 1:
                return (
                    'Ein Eintrag gewählt: ' +
                    (this.listSelection.getSelection()[0] as IReadPointInfo).name
                );
            default:
                return `${selectionCount} Einträge ausgewählt`;
        }
    }

    private RenderHeader(): ReactNode {
        return (
            <div>
                <h2>Lesepunkte</h2>
                <p>Auf dieser Seite k&ouml;nnen Sie die Lesepunkt bearbeiten.</p>
                <p>Das bedeutet neue RFID-Leser anzulegen, vorhandene zu bearbeiten und auch alte zu l&ouml;schen.<br />
                    Au&szlig;erdem k&ouml;nnen Sie hier zu Zuordnung zwischen den Lesepunkten und den Laufstrecken vornehmen.</p>
                <p><em>Bitte beachten Sie, dass die Leser zunächst innerhalb des Systems lizenziert werden m&uuml;ssen, damit sie Daten liefern d&uuml;rfen!</em></p>
            </div>);
    }

    private RenderToolbar(): ReactNode {
        return (
            // <FocusTrapZone>
            <CommandBar
                items={[
                    {
                        key: 'newItem',
                        text: 'Neu',
                        cacheKey: 'myCacheKey', // changing this key will invalidate this item's cache
                        iconProps: { iconName: 'Add' },
                        onClick: () => this.ToggleNewPanel(),
                    },
                    {
                        key: 'download',
                        text: 'Liste herunterladen',
                        iconProps: { iconName: 'Download' },
                        onClick: () => this.DownloadReadPoints(),
                    },
                    {
                        key: 'delete',
                        text: 'Löschen',
                        iconProps: { iconName: 'Delete' },
                        onClick: () => { this.DeleteReadPointAsync() },
                    },
                    {
                        key: 'refresh',
                        ariaLabel: "Refresh",
                        iconProps: { iconName: 'Refresh' },
                        onClick: () => { this.UpdateCurrentReadPointsAsync() },
                    }
                ]}
                farItems={[
                    // {
                    //     key: 'info',
                    //     text: 'Info',
                    //     // This needs an ariaLabel since it's icon-only
                    //     ariaLabel: 'Info',
                    //     iconOnly: true,
                    //     iconProps: { iconName: 'Info' },
                    //     onClick: () => console.log('Info'),
                    // },
                ]}
                ariaLabel="Aktionen"
            />

            // </FocusTrapZone>
        );
    }

    private RenderTable(): ReactNode {
        return (
            <div className={this.classNames.selectionDetails}>
                <Label>{this.state.selectionDetails}</Label>
                <MarqueeSelection selection={this.listSelection}>
                    <ShimmeredDetailsList
                        checkboxVisibility={CheckboxVisibility.always}
                        setKey="knownUsers"
                        layoutMode={DetailsListLayoutMode.fixedColumns}
                        constrainMode={ConstrainMode.unconstrained}
                        onRenderDetailsHeader={(headerProps, defaultRender)=> {
                            if (!headerProps || !defaultRender) {
                                //technically these may be undefined...
                                return null;
                            }
                            return defaultRender({
                                ...headerProps,
                                styles: {
                                    root: {
                                        selectors: {
                                            '.ms-DetailsHeader-cell': {
                                                whiteSpace: 'normal',
                                                textOverflow: 'clip',
                                                lineHeight: 'normal',
                                            },
                                            '.ms-DetailsHeader-cellTitle': {
                                                height: '100%',
                                                alignItems: 'center',
                                            },
                                        },
                                    },
                                },
                            }) 
                        }}
                        items={this.listSelection.getItems()}
                        columns={[
                            {
                                key: "keyId",
                                name: "Id",
                                ariaLabel: "Id",
                                fieldName: "key",
                                minWidth: 250,
                                isResizable: true,
                                columnActionsMode: ColumnActionsMode.clickable,
                                onRender: (item) => {
                                    { var userItem = item as IReadPointInfo; }
                                    return (<Link data-selection-invoke={true} >{userItem.key}</Link>)
                                }
                            }, {
                                key: "keyAccount",
                                name: "Login",
                                ariaLabel: "Login",
                                fieldName: "account",
                                minWidth: 100,
                                isResizable: true
                            },
                            {
                                key: "keyName",
                                name: "Name",
                                ariaLabel: "Name",
                                fieldName: "name",
                                minWidth: 100,
                                isResizable: true
                            }, {
                                key: "keyLength",
                                name: "Ist Zwischenmessung?",
                                ariaLabel: "Ist Zwischenmessung?",
                                fieldName: "isIntermediate",
                                minWidth: 130,
                                isResizable: true,
                                onRender: (item => {
                                    return <div><Checkbox checked={(item as IReadPointInfo).isIntermediate} disabled /></div>
                                })
                            }
                            , {
                                key: "keyDistanceToStart",
                                name: "Abstand zum Startpunkt",
                                ariaLabel: "Abstand zum Startpunkt",
                                minWidth: 150,
                                isResizable: true,
                                onRender: (item => (item as IReadPointInfo).isIntermediate ? (item as IReadPointInfo).distanceToStart + " m" : "-")
                            }, {
                                key: "keyAssignedTrackCount",
                                name: "Anzahl zugeordneter Laufstrecken",
                                ariaLabel: "Anzahl zugeordneter Laufstrecken",
                                minWidth: 150,
                                isResizable: true,
                                onRender: (item => <Link data-selection-invoke={false} onClick={() => this.EditAssignment((item as IReadPointInfo).key)} >{(item as IReadPointInfo).assignedRaceTracks?.length}</Link>)
                            },
                            {
                                key: "keyUpdatePassword",
                                name: "Passwort ändern",
                                ariaLabel: "Passwort ändern",
                                minWidth: 150,
                                isResizable: false,
                                columnActionsMode: ColumnActionsMode.clickable,
                                onRender: (item) => {
                                    return (<Link data-selection-invoke={false} onClick={() => this.ChangePassword((item as IReadPointInfo).accountId)} ><PasswordIcon /></Link>)
                                }
                            }
                        ]}
                        selection={this.listSelection}
                        selectionMode={SelectionMode.multiple}
                        enterModalSelectionOnTouch={true}
                        enableShimmer={!this.state.knownReadPoints}
                        ariaLabelForShimmer="Content is being fetched"
                        ariaLabelForGrid="Item details"
                        selectionPreservedOnEmptyClick={true}
                        onItemInvoked={this.ListItemInvoked}
                        listProps={{
                            renderedWindowsAhead: 0,
                            renderedWindowsBehind: 0,
                        }}
                    />
                </MarqueeSelection>
            </div>
        );
    }

    EditAssignment(key: string | number): void {
        this.setState({
            ...this.state,
            isEditAssignmentPanelOpen: true,
            readPointId: key as string,
        })
    }

    private ChangePassword(accountId: string | number | undefined): void {
        this.setState({
            ... this.state,
            isChangePasswordPanelOpen: true,
            changePasswordAccountKey: accountId,
        })
    }

    private RenderEditPanel(): ReactNode {
        if (this.state.isEditPanelOpen && this.state.readPointId !== undefined) {
            return (
                <Panel
                    type={PanelType.medium}
                    isBlocking={true}
                    isOpen={this.state.isEditPanelOpen}
                    hasCloseButton={false}
                    // You MUST provide this prop! Otherwise screen readers will just say "button" with no label.
                    closeButtonAriaLabel="Close">
                    <EditReadPoint onUpdated={this.ReadPointChanged} onCancel={this.ReadPointChangeCanceled} readPointId={this.state.readPointId} />
                </Panel>)
        }
        else {
            <div />
        }
    }

    private ReadPointChanged(): void {
        this.setState({
            ...this.state,
            isNewPanelOpen: false,
            isEditPanelOpen: false,
            isEditAssignmentPanelOpen: false,
            isChangePasswordPanelOpen: false,
        });
        this.UpdateCurrentReadPointsAsync();
    }

    private ReadPointChangeCanceled(): void {
        this.setState({
            ...this.state,
            isNewPanelOpen: false,
            isEditPanelOpen: false,
            isEditAssignmentPanelOpen: false,
            isChangePasswordPanelOpen: false,
        })
    }

    private ListItemInvoked(item: IReadPointInfo): void {
        console.log(`Item invoked: ${item.key}`);
        this.setState({
            ... this.state,
            isEditPanelOpen: true,
            readPointId: String(item.key)
        })
    };

    private async DeleteReadPointAsync(): Promise<void> {
        try {
            var selectedItems = this.listSelection.getSelection();
            for (var i = 0; i < selectedItems.length; i++) {
                await this.props.webApiClient.DeleteReadPointAsync(this.props.userStore.accessToken, selectedItems[i].key as string)
            }
            // this.listSelection.getSelection().forEach(async element => await this.props.webApiClient.DeleteUserAsync(this.props.userStore.accessToken, element.key as string));
        }
        catch (reason) {
            var error = "Ein unbekannter Fehler ist aufgetreten.";

            if (reason.body as ErrorInfo !== null) {
                var errorInfo = reason.body as ErrorInfo;
                error = errorInfo.errors !== null && errorInfo.errors !== undefined
                    ? Object.entries(errorInfo.errors).map(([k, v]) => (v.errorMessage)).join(", ") : "Keine Errorinfo erhalten";
            }

            toast.error(`Konnte die ausgewählten Daten nicht löschen: ${error}`);
        }

        await this.UpdateCurrentReadPointsAsync();
    }

    private ToggleNewPanel(): void {
        this.setState({
            ...this.state,
            isNewPanelOpen: true,
        });
    }

    private DownloadReadPoints(): void {
        var lines = [];
        lines.push("ID;Login;Name;Ist Zwischenmessung;Abstand zum Startpunkt;Anzahl zugeordneter Laufstrecken\r\n");

        if (this.state.knownReadPoints !== undefined) {
            this.state.knownReadPoints!.forEach(element => {
                lines.push(`${element.key};${element.account};${element.name};${element.isIntermediate};${element.distanceToStart};${element.assignedRaceTracks?.length}\r\n`);
            });
        }

        var blob = new Blob(lines, { type: 'text/csv' })

        const a = document.createElement('a')
        a.download = "ReadPoints.csv"
        a.href = window.URL.createObjectURL(blob)
        const clickEvt = new MouseEvent('click', {
            view: window,
            bubbles: true,
            cancelable: true,
        })
        a.dispatchEvent(clickEvt)
        a.remove()
    }

    private async UpdateCurrentReadPointsAsync(): Promise<void> {
        console.log("Refreshing the current known read points.");
        var readPoints = await this.props.webApiClient.GetAllReadPointsAsync(this.props.userStore.accessToken);

        var readPointInfo: IReadPointInfo[] = [];
        readPoints.forEach(element => {
            var trackInfo: IRaceTrackInfo[] = [];

            element.assignedRaceTracks?.forEach(track => {
                trackInfo.push(
                    {
                        key: track.id!,
                        name: track.trackName!,
                        length: track.lengthInM!,
                        lowerBounds: track.lowerTimeBoundMs!,
                        upperBounds: track.upperTimeBoundMs!,
                        // TODO: Must retrieve the assigned read points.
                        assignedReadPoints: undefined,                        
                        conflictingTrackIds: undefined
                    }
                );
            })

            readPointInfo.push({
                key: element.id!,
                name: element.pointIdentifier!,
                isIntermediate: element.isIntermediate!,
                assignedRaceTracks: trackInfo,
                account: element.account?.login!,
                accountId: element.account?.id,
                distanceToStart: element.distanceToStartInM,
            });
        });

        this.listSelection.setItems(readPointInfo);
        this.setState({
            ...this.state,
            knownReadPoints: readPointInfo,
        })
    }
}